import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { USER_ALPHA_LOGIN, USER_ALPHA_LOGIN_VERIFY } from '../../helper/gql';
import { maskEmail } from '../../helper/form';

type Step = 'email' | 'code' | 'success';

interface AlphaLoginState {
  step: Step;
  email: string;
  code: string;
  emailError: string;
  codeError: string;
  isLoading: boolean;
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidCode = (code: string): boolean => {
  return /^\d{6}$/.test(code);
};

export const AlphaLogin: React.FC = () => {
  const history = useHistory();
  const [state, setState] = useState<AlphaLoginState>({
    step: 'email',
    email: '',
    code: '',
    emailError: '',
    codeError: '',
    isLoading: false
  });

  const [alphaLogin] = useMutation(USER_ALPHA_LOGIN);
  const [alphaLoginVerify] = useMutation(USER_ALPHA_LOGIN_VERIFY);

  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (state.step !== 'success') return;

    if (countdown <= 0) {
      history.push('/dashboard');
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [state.step, countdown, history]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState(prev => ({ ...prev, email: value, emailError: '' }));
  }, []);

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setState(prev => ({ ...prev, code: value, codeError: '' }));
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state.email) {
      setState(prev => ({ ...prev, emailError: 'Email is required' }));
      return;
    }

    if (!isValidEmail(state.email)) {
      setState(prev => ({ ...prev, emailError: 'Please enter a valid email address' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, emailError: '' }));

    try {
      const { data } = await alphaLogin({
        variables: { input: { email: state.email } }
      });

      if (data?.userAlphaLogin?.success) {
        setState(prev => ({ ...prev, step: 'code', isLoading: false }));
      } else {
        setState(prev => ({
          ...prev,
          emailError: data?.userAlphaLogin?.message || 'Failed to send verification code',
          isLoading: false
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        emailError: error instanceof Error ? error.message : 'An error occurred',
        isLoading: false
      }));
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state.code) {
      setState(prev => ({ ...prev, codeError: 'Verification code is required' }));
      return;
    }

    if (!isValidCode(state.code)) {
      setState(prev => ({ ...prev, codeError: 'Please enter a valid 6-digit code' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, codeError: '' }));

    try {
      const { data } = await alphaLoginVerify({
        variables: { input: { email: state.email, passcode: state.code } }
      });

      if (data?.userAlphaLoginVerify?.success) {
        if (data.userAlphaLoginVerify.token) {
          localStorage.setItem('authToken', data.userAlphaLoginVerify.token);
          window.dispatchEvent(new Event('authChange'));
        }
        setState(prev => ({ ...prev, step: 'success', isLoading: false }));
      } else {
        setState(prev => ({
          ...prev,
          codeError: data?.userAlphaLoginVerify?.message || 'Verification failed',
          isLoading: false
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        codeError: error instanceof Error ? error.message : 'An error occurred',
        isLoading: false
      }));
    }
  };

  const getStepNumber = (): number => {
    switch (state.step) {
      case 'email': return 1;
      case 'code': return 2;
      case 'success': return 2;
      default: return 1;
    }
  };

  const renderEmailStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Alpha Login</h2>
        <p>Enter your email to sign in</p>
      </div>
      <form onSubmit={handleEmailSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            type="email"
            value={state.email}
            onChange={handleEmailChange}
            placeholder="Enter your email"
            autoFocus
          />
        </div>
        {state.emailError && (
          <div className="error-message">{state.emailError}</div>
        )}
        <button type="submit" className="auth-button" disabled={state.isLoading}>
          {state.isLoading ? 'Sending...' : 'Continue'}
        </button>
      </form>
    </div>
  );

  const renderCodeStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Verify Your Email</h2>
        <p>Enter the 6-digit code sent to {maskEmail(state.email)}</p>
      </div>
      <form onSubmit={handleCodeSubmit}>
        <div className="form-group">
          <label htmlFor="code">Verification Code</label>
          <input
            id="code"
            type="text"
            value={state.code}
            onChange={handleCodeChange}
            placeholder="Enter 6-digit code"
            maxLength={6}
            autoFocus
          />
        </div>
        {state.codeError && (
          <div className="error-message">{state.codeError}</div>
        )}
        <button type="submit" className="auth-button" disabled={state.isLoading}>
          {state.isLoading ? 'Verifying...' : 'Verify'}
        </button>
        <button
          type="button"
          onClick={() => setState(prev => ({ ...prev, step: 'email' }))}
          className="auth-button-secondary"
          disabled={state.isLoading}
        >
          Back
        </button>
      </form>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Success!</h2>
        <p>Redirecting to dashboard in {countdown} seconds...</p>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (state.step) {
      case 'email': return renderEmailStep();
      case 'code': return renderCodeStep();
      case 'success': return renderSuccessStep();
      default: return renderEmailStep();
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="progress-bar">
          <div className="progress-indicator">
            Step {getStepNumber()} of 2
          </div>
          <div className="progress-line">
            <div
              className="progress-fill"
              style={{ width: `${(getStepNumber() / 2) * 100}%` }}
            />
          </div>
        </div>

        {renderStep()}

        <div className="auth-footer">
          <p>Don't have an account? <a href="/alpha/join">Sign up</a></p>
          <p><a href="/">← Back to Home</a></p>
        </div>
      </div>
    </div>
  );
};